import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { api } from '../App';
import { BookOpen, LogOut, Users, Building2, Plus, GraduationCap, BarChart3, TrendingUp, UserPlus, Eye, ClipboardList, Send, FileText, Download, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];

const ROLE_LABELS = {
  institution_admin: 'Kurum Yoneticisi',
  mudur: 'Mudur',
  mudur_yardimcisi: 'Mudur Yrd.',
  rehber_ogretmen: 'PDR',
  psikolog: 'Psikolog',
  teacher: 'Ogretmen',
  admin: 'Admin'
};

export default function InstitutionDashboard({ user, logout, setUser }) {
  const navigate = useNavigate();
  const [institution, setInstitution] = useState(null);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [studentReports, setStudentReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [selectedClassForAssign, setSelectedClassForAssign] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignType, setAssignType] = useState('students');
  
  // Class report states
  const [classProfileReport, setClassProfileReport] = useState(null);
  const [classProfileLoading, setClassProfileLoading] = useState(false);
  const [selectedClassForReport, setSelectedClassForReport] = useState(null);
  const [classTemperamentReport, setClassTemperamentReport] = useState(null);
  const [classReportDialogOpen, setClassReportDialogOpen] = useState(false);
  const [expandedStudent, setExpandedStudent] = useState(null);

  const [classForm, setClassForm] = useState({ name: '', grade_level: '' });
  const [bulkUserText, setBulkUserText] = useState('');
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'student', birth_date: '', grade_level: '' });
  const [assessmentAssigning, setAssessmentAssigning] = useState(false);
  const [parentDialogOpen, setParentDialogOpen] = useState(false);
  const [parentForm, setParentForm] = useState({ name: '', email: '', password: '', student_id: '' });

  const roleLabel = ROLE_LABELS[user.role] || user.role;
  const canViewReports = ['institution_admin', 'mudur', 'mudur_yardimcisi', 'rehber_ogretmen', 'psikolog', 'admin', 'super_admin'].includes(user.role);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [instResponse, classesResponse, analyticsResponse] = await Promise.all([
        api.get('/institutions/my'),
        api.get('/institutions/classes'),
        api.get('/analytics/institution').catch(() => ({ data: null }))
      ]);
      
      setInstitution(instResponse.data);
      setClasses(classesResponse.data);
      setAnalytics(analyticsResponse.data);
      
      if (instResponse.data?.id) {
        try {
          const usersResponse = await api.get(`/institutions/${instResponse.data.id}/users`);
          const allUsers = usersResponse.data || [];
          setTeachers(allUsers.filter(u => ['teacher', 'mudur', 'mudur_yardimcisi', 'rehber_ogretmen', 'psikolog'].includes(u.role)));
          setStudents(allUsers.filter(u => u.role === 'student'));
        } catch (e) { /* ignore */ }
      }
      
      // Fetch student reports if user has access
      if (canViewReports) {
        try {
          const reportsRes = await api.get('/reports/institution/students');
          setStudentReports(reportsRes.data?.reports || []);
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      toast.error('Veriler yuklenirken hata olustu');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    try {
      await api.post('/institutions/classes', classForm);
      toast.success('Sinif basariyla olusturuldu');
      setDialogOpen(false);
      setClassForm({ name: '', grade_level: '' });
      fetchData();
    } catch (error) {
      toast.error('Sinif olusturulurken hata olustu');
    }
  };

  const handleAddUser = async () => {
    if (!userForm.name || !userForm.email) { toast.error('Ad ve email zorunludur'); return; }
    try {
      const password = userForm.password || (userForm.role === 'student' ? 'Ogrenci123!' : 'Test123!');
      const userData = { ...userForm, password };
      if (!userData.birth_date) delete userData.birth_date;
      if (!userData.grade_level) delete userData.grade_level;
      await api.post('/institutions/users/bulk', { users: [userData] });
      toast.success('Kullanici basariyla eklendi');
      setAddUserDialogOpen(false);
      setUserForm({ name: '', email: '', password: '', role: 'student', birth_date: '', grade_level: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanici eklenemedi');
    }
  };

  const handleBulkUserCreate = async () => {
    try {
      const lines = bulkUserText.trim().split('\n');
      const users = lines.map(line => {
        const [role, name, email, password] = line.split(',').map(s => s.trim());
        return { role, name, email, password: password || (role === 'student' ? 'Ogrenci123!' : 'Test123!') };
      }).filter(u => u.name && u.email);
      const response = await api.post('/institutions/users/bulk', { users });
      toast.success(`${response.data.created?.length || 0} kullanici olusturuldu`);
      setBulkDialogOpen(false);
      setBulkUserText('');
      fetchData();
    } catch (error) {
      toast.error('Toplu kullanici olusturma basarisiz');
    }
  };

  const handleAssignAssessment = async (classId, studentIds) => {
    setAssessmentAssigning(true);
    try {
      const data = { assessment_type: 'student_self' };
      if (classId) data.class_id = classId;
      if (studentIds?.length) data.student_ids = studentIds;
      const response = await api.post('/assessments/assign', data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Envanter atama basarisiz');
    } finally {
      setAssessmentAssigning(false);
    }
  };

  const handleFillAssessmentForStudent = async (student) => {
    try {
      // Check student info
      const infoRes = await api.get(`/students/${student.id}/assessment-info`);
      const info = infoRes.data;
      
      if (info.has_report) {
        toast.info('Bu ogrencinin raporu zaten mevcut');
        if (info.report_id) navigate(`/report/${info.report_id}`);
        return;
      }
      
      // Create assessment for this student
      const assessmentType = info.needs_observer ? 'teacher_about_student' : 'student_self';
      const assessRes = await api.post('/assessments', {
        assessment_type: assessmentType,
        target_user_id: student.id
      });
      
      // Navigate to observer assessment page
      navigate(`/observer-assessment/${assessRes.data.id}?target=${student.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Envanter baslatilamadi');
    }
  };

  const handleCreateParent = async () => {
    if (!parentForm.name || !parentForm.email) { toast.error('Veli adi ve emaili zorunludur'); return; }
    try {
      await api.post('/parents/create-with-link', parentForm);
      toast.success('Veli hesabi olusturuldu ve ogrenciyle eslestirildi');
      setParentDialogOpen(false);
      setParentForm({ name: '', email: '', password: '', student_id: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Veli hesabi olusturulamadi');
    }
  };

  const loadClassProfileReport = async (cls) => {
    setSelectedClassForReport(cls);
    setClassProfileLoading(true);
    setClassProfileReport(null);
    setClassTemperamentReport(null);
    setClassReportDialogOpen(true);
    try {
      const [profileRes, tempRes] = await Promise.all([
        api.get(`/analytics/classes/${cls.id}/profile-report`),
        api.get(`/analytics/classes/${cls.id}/temperament-report`)
      ]);
      setClassProfileReport(profileRes.data);
      setClassTemperamentReport(tempRes.data);
    } catch (error) {
      toast.error('Sinif raporu yuklenemedi');
    } finally {
      setClassProfileLoading(false);
    }
  };

  const handleStopImpersonation = async () => {
    try {
      const response = await api.post('/impersonate/stop');
      const { access_token, user: adminUser } = response.data;
      localStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(adminUser);
      toast.success('Super admin hesabiniza dondunuz');
      navigate('/');
    } catch (error) {
      toast.error('Oturum sonlandirilamadi');
    }
  };

  const pieChartData = analytics?.overall_distribution?.distribution?.map(item => ({
    name: item.name, value: item.count, percentage: item.percentage
  })) || [];

  const classComparisonData = analytics?.class_analytics?.map(cls => ({
    name: cls.class_name, total: cls.total_students, completed: cls.students_with_reports
  })) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Mizac360</h1>
              <p className="text-sm text-slate-600">{roleLabel} Paneli</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user.impersonation?.active && (
              <Badge className="bg-orange-100 text-orange-700 animate-pulse">Taklit: {user.impersonation.admin_name}</Badge>
            )}
            <Badge variant="outline" className="bg-blue-50 text-blue-700" data-testid="user-role-badge">{roleLabel}</Badge>
            <span className="text-sm text-slate-600">{user.name}</span>
            <Button variant="outline" size="sm" onClick={() => navigate('/coach')} className="bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200">
              <FileText className="w-4 h-4 mr-1" /> Koc
            </Button>
            {user.impersonation?.active && (
              <Button variant="outline" size="sm" onClick={handleStopImpersonation} className="bg-orange-50 border-orange-200">Taklidi Durdur</Button>
            )}
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" /> Cikis</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Kurum Yonetimi</h2>
          <p className="text-slate-600">{institution?.name || 'Kurumunuzu'} yonetin ve analiz edin</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className={`grid w-full ${canViewReports ? 'grid-cols-5' : 'grid-cols-4'}`}>
              <TabsTrigger value="overview" data-testid="tab-overview">Genel Bakis</TabsTrigger>
              <TabsTrigger value="classes" data-testid="tab-classes">Sinif Yonetimi</TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users">Kullanicilar</TabsTrigger>
              <TabsTrigger value="assessments" data-testid="tab-assessments">Envanter</TabsTrigger>
              {canViewReports && <TabsTrigger value="reports" data-testid="tab-reports">Raporlar</TabsTrigger>}
            </TabsList>

            {/* ===== OVERVIEW TAB ===== */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid md:grid-cols-4 gap-6">
                <Card className="shadow-lg border-none">
                  <CardHeader className="pb-3">
                    <CardDescription className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Toplam Sinif</CardDescription>
                    <CardTitle className="text-3xl">{classes.length || 0}</CardTitle>
                  </CardHeader>
                </Card>
                <Card className="shadow-lg border-none">
                  <CardHeader className="pb-3">
                    <CardDescription className="flex items-center gap-2"><Users className="w-4 h-4" /> Toplam Ogrenci</CardDescription>
                    <CardTitle className="text-3xl text-blue-600">{students.length || 0}</CardTitle>
                  </CardHeader>
                </Card>
                <Card className="shadow-lg border-none">
                  <CardHeader className="pb-3">
                    <CardDescription className="flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Personel</CardDescription>
                    <CardTitle className="text-3xl text-emerald-600">{teachers.length || 0}</CardTitle>
                  </CardHeader>
                </Card>
                <Card className="shadow-lg border-none">
                  <CardHeader className="pb-3">
                    <CardDescription>Tamamlanma</CardDescription>
                    <CardTitle className="text-3xl text-purple-600">
                      {analytics?.total_students > 0 ? Math.round((analytics.overall_distribution?.students_with_reports || 0) / analytics.total_students * 100) : 0}%
                    </CardTitle>
                  </CardHeader>
                </Card>
              </div>

              {pieChartData.length > 0 && (
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="shadow-lg border-none">
                    <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" /> Mizac Dagilimi</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie data={pieChartData} cx="50%" cy="50%" labelLine={false} label={({ name, percentage }) => `${name.split(' / ')[0]}: ${percentage}%`} outerRadius={80} fill="#8884d8" dataKey="value">
                            {pieChartData.map((_, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card className="shadow-lg border-none">
                    <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-600" /> Sinif Katilim</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={classComparisonData}>
                          <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis /><Tooltip /><Legend />
                          <Bar dataKey="total" fill="#94a3b8" name="Toplam" />
                          <Bar dataKey="completed" fill="#10b981" name="Tamamlanan" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* ===== CLASSES TAB ===== */}
            <TabsContent value="classes" className="space-y-6">
              <Card className="shadow-lg border-none">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div><CardTitle>Sinif Yonetimi</CardTitle><CardDescription>Siniflarinizi yonetin</CardDescription></div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-gradient-to-r from-blue-600 to-emerald-600" data-testid="create-class-btn"><Plus className="w-4 h-4 mr-2" /> Yeni Sinif</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Yeni Sinif Olustur</DialogTitle></DialogHeader>
                        <form onSubmit={handleCreateClass} className="space-y-4">
                          <div><Label>Sinif Adi</Label><Input placeholder="Orn: 7-A" value={classForm.name} onChange={(e) => setClassForm({...classForm, name: e.target.value})} required data-testid="class-name-input" /></div>
                          <div><Label>Sinif Seviyesi</Label><Input placeholder="Orn: 7" value={classForm.grade_level} onChange={(e) => setClassForm({...classForm, grade_level: e.target.value})} required data-testid="class-level-input" /></div>
                          <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-emerald-600" data-testid="submit-class-btn">Olustur</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {classes.length > 0 ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {classes.map((cls) => (
                        <Card key={cls.id} className="border shadow-sm hover:shadow-md transition-shadow">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg">{cls.name}</CardTitle>
                            <CardDescription>{cls.grade_level}. Sinif</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between"><span className="text-slate-600">Ogretmen:</span><Badge variant="outline">{cls.teacher_ids?.length || 0}</Badge></div>
                              <div className="flex justify-between"><span className="text-slate-600">Ogrenci:</span><Badge variant="outline">{cls.student_ids?.length || 0}</Badge></div>
                              <div className="flex gap-2 pt-2">
                                <Button variant="outline" size="sm" className="flex-1 bg-blue-50 border-blue-200" data-testid={`assign-btn-${cls.id}`}
                                  onClick={() => { setSelectedClassForAssign(cls); setAssignType('students'); setSelectedUsers([]); setAssignDialogOpen(true); }}>
                                  <UserPlus className="w-3 h-3 mr-1" /> Ata
                                </Button>
                                <Button variant="outline" size="sm" className="flex-1 bg-emerald-50 border-emerald-200" data-testid={`assessment-btn-${cls.id}`}
                                  onClick={() => handleAssignAssessment(cls.id, null)}>
                                  <Send className="w-3 h-3 mr-1" /> Envanter
                                </Button>
                                {canViewReports && (
                                  <Button variant="outline" size="sm" className="flex-1 bg-purple-50 border-purple-200" data-testid={`class-report-btn-${cls.id}`}
                                    onClick={() => loadClassProfileReport(cls)}>
                                    <BarChart3 className="w-3 h-3 mr-1" /> Rapor
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12"><Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">Henuz sinif olusturulmamis</p></div>
                  )}

                  {/* Assignment Dialog */}
                  <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{selectedClassForAssign?.name} - {assignType === 'students' ? 'Ogrenci' : 'Ogretmen'} Ata</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="flex gap-2">
                          <Button variant={assignType === 'students' ? 'default' : 'outline'} size="sm" onClick={() => { setAssignType('students'); setSelectedUsers([]); }}>Ogrenci</Button>
                          <Button variant={assignType === 'teachers' ? 'default' : 'outline'} size="sm" onClick={() => { setAssignType('teachers'); setSelectedUsers([]); }}>Ogretmen</Button>
                        </div>
                        <div className="border rounded-lg max-h-60 overflow-y-auto">
                          {(assignType === 'students' ? students : teachers).length > 0 ? (
                            <div className="divide-y">
                              {(assignType === 'students' ? students : teachers)
                                .filter(u => !selectedClassForAssign?.[assignType === 'students' ? 'student_ids' : 'teacher_ids']?.includes(u.id))
                                .map(usr => (
                                <div key={usr.id} className="flex items-center gap-3 p-3 hover:bg-slate-50">
                                  <Checkbox checked={selectedUsers.includes(usr.id)} onCheckedChange={(checked) => setSelectedUsers(checked ? [...selectedUsers, usr.id] : selectedUsers.filter(id => id !== usr.id))} />
                                  <div><p className="font-medium text-sm">{usr.name}</p><p className="text-xs text-slate-500">{usr.email}</p></div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-slate-500"><Users className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">Atanacak kullanici bulunamadi</p></div>
                          )}
                        </div>
                        <Button className="w-full bg-gradient-to-r from-blue-600 to-emerald-600" disabled={selectedUsers.length === 0} data-testid="confirm-assign-btn"
                          onClick={async () => {
                            try {
                              const endpoint = assignType === 'students' ? '/institutions/classes/assign-students' : '/institutions/classes/assign-teachers';
                              await api.post(endpoint, { class_id: selectedClassForAssign.id, [assignType === 'students' ? 'student_ids' : 'teacher_ids']: selectedUsers });
                              toast.success(`${selectedUsers.length} kisi atandi`);
                              setAssignDialogOpen(false);
                              fetchData();
                            } catch (error) { toast.error('Atama yapilamadi'); }
                          }}>
                          {selectedUsers.length} Kisiyi Ata
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===== USERS TAB ===== */}
            <TabsContent value="users" className="space-y-6">
              <Card className="shadow-lg border-none">
                <CardHeader>
                  <div className="flex justify-between items-center flex-wrap gap-3">
                    <div><CardTitle>Kullanici Yonetimi</CardTitle><CardDescription>Ogrenci, ogretmen ve personel hesaplarini yonetin</CardDescription></div>
                    <div className="flex gap-2">
                      <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="bg-gradient-to-r from-blue-600 to-emerald-600" data-testid="add-user-btn"><UserPlus className="w-4 h-4 mr-2" /> Kullanici Ekle</Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Yeni Kullanici Ekle</DialogTitle><DialogDescription>Kurumunuza yeni bir kullanici ekleyin</DialogDescription></DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Rol *</Label>
                              <select className="w-full border rounded-md px-3 py-2 mt-1" value={userForm.role} data-testid="user-role-select" onChange={(e) => setUserForm({...userForm, role: e.target.value})}>
                                <option value="student">Ogrenci</option>
                                <option value="teacher">Ogretmen</option>
                                <option value="rehber_ogretmen">Rehber Ogretmen (PDR)</option>
                                <option value="psikolog">Psikolog</option>
                                <option value="mudur">Mudur</option>
                                <option value="mudur_yardimcisi">Mudur Yardimcisi</option>
                              </select>
                            </div>
                            <div><Label>Ad Soyad *</Label><Input value={userForm.name} onChange={(e) => setUserForm({...userForm, name: e.target.value})} placeholder="Ad Soyad" data-testid="user-name-input" /></div>
                            <div><Label>Email *</Label><Input type="email" value={userForm.email} onChange={(e) => setUserForm({...userForm, email: e.target.value})} placeholder="email@okul.com" data-testid="user-email-input" /></div>
                            <div><Label>Sifre (bos birakilirsa varsayilan atanir)</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm({...userForm, password: e.target.value})} placeholder="Sifre" data-testid="user-password-input" /></div>
                            {userForm.role === 'student' && (
                              <>
                                <div>
                                  <Label>Dogum Tarihi</Label>
                                  <Input type="date" value={userForm.birth_date} onChange={(e) => setUserForm({...userForm, birth_date: e.target.value})} data-testid="user-birthdate-input" />
                                  <p className="text-xs text-slate-500 mt-1">10 yas alti ogrenciler icin ogretmen/veli envanteri doldurur</p>
                                </div>
                                <div>
                                  <Label>Sinif Seviyesi</Label>
                                  <select className="w-full border rounded-md px-3 py-2 mt-1" value={userForm.grade_level}
                                    data-testid="user-grade-select"
                                    onChange={(e) => setUserForm({...userForm, grade_level: e.target.value})}>
                                    <option value="">Seciniz</option>
                                    <option value="anaokulu">Anaokulu</option>
                                    <option value="1">1. Sinif</option>
                                    <option value="2">2. Sinif</option>
                                    <option value="3">3. Sinif</option>
                                    <option value="4">4. Sinif</option>
                                    <option value="5">5. Sinif</option>
                                    <option value="6">6. Sinif</option>
                                    <option value="7">7. Sinif</option>
                                    <option value="8">8. Sinif</option>
                                    <option value="9">9. Sinif</option>
                                    <option value="10">10. Sinif</option>
                                    <option value="11">11. Sinif</option>
                                    <option value="12">12. Sinif</option>
                                  </select>
                                </div>
                              </>
                            )}
                            <Button onClick={handleAddUser} className="w-full bg-gradient-to-r from-blue-600 to-emerald-600" data-testid="submit-user-btn">Kullanici Ekle</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="border-purple-200 bg-purple-50" data-testid="bulk-user-btn"><Users className="w-4 h-4 mr-2" /> Toplu Ekle</Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader><DialogTitle>Toplu Kullanici Olusturma</DialogTitle><DialogDescription>Her satira: rol,ad soyad,email,sifre</DialogDescription></DialogHeader>
                          <div className="space-y-4">
                            <textarea className="w-full h-48 p-3 border rounded-md font-mono text-sm" data-testid="bulk-user-textarea"
                              placeholder={`teacher,Ahmet Yilmaz,ahmet@okul.com,Sifre123\nstudent,Ayse Demir,ayse@okul.com,Sifre123`}
                              value={bulkUserText} onChange={(e) => setBulkUserText(e.target.value)} />
                            <div className="bg-blue-50 p-3 rounded-lg text-sm">
                              <p className="font-semibold mb-1">Roller:</p>
                              <div className="grid grid-cols-2 gap-1 text-slate-700">
                                <span><code>student</code> - Ogrenci</span><span><code>teacher</code> - Ogretmen</span>
                                <span><code>rehber_ogretmen</code> - PDR</span><span><code>psikolog</code> - Psikolog</span>
                                <span><code>mudur</code> - Mudur</span><span><code>mudur_yardimcisi</code> - Mudur Yrd.</span>
                              </div>
                            </div>
                            <Button onClick={handleBulkUserCreate} className="w-full bg-gradient-to-r from-purple-600 to-pink-600" data-testid="submit-bulk-btn">Kullanicilari Olustur</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {teachers.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-semibold text-slate-700 mb-3">Personel ({teachers.length})</h3>
                      <div className="space-y-2">
                        {teachers.map(t => (
                          <div key={t.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                            <div><p className="font-medium text-sm">{t.name}</p><p className="text-xs text-slate-500">{t.email}</p></div>
                            <Badge className={t.role === 'mudur' ? 'bg-red-100 text-red-700' : t.role === 'rehber_ogretmen' ? 'bg-blue-100 text-blue-700' : t.role === 'psikolog' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}>{ROLE_LABELS[t.role] || t.role}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-slate-700">Ogrenciler ({students.length})</h3>
                      <Dialog open={parentDialogOpen} onOpenChange={setParentDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="bg-orange-50 border-orange-200" data-testid="add-parent-btn">
                            <UserPlus className="w-3 h-3 mr-1" /> Veli Ekle
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Veli Hesabi Olustur</DialogTitle><DialogDescription>Velinin hesabini olusturun ve ogrenciyle eslestirin</DialogDescription></DialogHeader>
                          <div className="space-y-4">
                            <div><Label>Veli Adi *</Label><Input value={parentForm.name} onChange={(e) => setParentForm({...parentForm, name: e.target.value})} placeholder="Veli adi" data-testid="parent-name-input" /></div>
                            <div><Label>Email *</Label><Input type="email" value={parentForm.email} onChange={(e) => setParentForm({...parentForm, email: e.target.value})} placeholder="veli@email.com" data-testid="parent-email-input" /></div>
                            <div><Label>Sifre</Label><Input type="password" value={parentForm.password} onChange={(e) => setParentForm({...parentForm, password: e.target.value})} placeholder="Bos birakilirsa: Veli123!" data-testid="parent-password-input" /></div>
                            <div>
                              <Label>Ogrenci</Label>
                              <select className="w-full border rounded-md px-3 py-2 mt-1" value={parentForm.student_id} onChange={(e) => setParentForm({...parentForm, student_id: e.target.value})} data-testid="parent-student-select">
                                <option value="">Ogrenci secin</option>
                                {students.map(s => (<option key={s.id} value={s.id}>{s.name} ({s.email})</option>))}
                              </select>
                            </div>
                            <Button onClick={handleCreateParent} className="w-full bg-gradient-to-r from-orange-500 to-amber-500" data-testid="submit-parent-btn">Veli Olustur ve Esle</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    {students.length > 0 ? (
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {students.map(s => {
                          const age = s.birth_date ? Math.floor((Date.now() - new Date(s.birth_date).getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : null;
                          const isYoung = (age !== null && age < 10) || ['anaokulu', '1', '2', '3', '4'].includes(String(s.grade_level || '').toLowerCase());
                          return (
                            <div key={s.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="font-medium text-sm">{s.name}</p>
                                <div className="flex gap-2 items-center mt-0.5">
                                  <p className="text-xs text-slate-500">{s.email}</p>
                                  {s.grade_level && <Badge variant="outline" className="text-xs">{s.grade_level}. Sinif</Badge>}
                                  {isYoung && <Badge className="text-xs bg-amber-100 text-amber-700">Gozlemci Gerekli</Badge>}
                                  {age !== null && <span className="text-xs text-slate-400">{age} yas</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {canViewReports && studentReports.find(r => r.target_user_id === s.id) ? (
                                  <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => navigate(`/report/${studentReports.find(r => r.target_user_id === s.id)?.id}`)}>
                                    <Eye className="w-3 h-3 mr-1" /> Rapor
                                  </Button>
                                ) : (
                                  isYoung && canViewReports && (
                                    <Button variant="outline" size="sm" className="bg-amber-50 border-amber-200 text-amber-700" data-testid={`fill-assessment-${s.id}`}
                                      onClick={() => handleFillAssessmentForStudent(s)}>
                                      <ClipboardList className="w-3 h-3 mr-1" /> Envanter Doldur
                                    </Button>
                                  )
                                )}
                                <Badge variant="outline" className="bg-blue-50 text-blue-700">Ogrenci</Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-slate-50 rounded-lg"><Users className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">Henuz ogrenci eklenmemis</p></div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===== ASSESSMENTS TAB ===== */}
            <TabsContent value="assessments" className="space-y-6">
              <Card className="shadow-lg border-none">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-blue-600" /> Envanter Yonlendirme</CardTitle>
                  <CardDescription>Ogrencilere mizac envanteri atayin</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-3">Sinif Bazinda Atama</h3>
                    {classes.length > 0 ? (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {classes.map(cls => (
                          <Card key={cls.id} className="border shadow-sm">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start mb-3">
                                <div><h4 className="font-semibold">{cls.name}</h4><p className="text-sm text-slate-500">{cls.grade_level}. Sinif - {cls.student_ids?.length || 0} ogrenci</p></div>
                              </div>
                              <Button size="sm" className="w-full bg-gradient-to-r from-blue-600 to-emerald-600" disabled={assessmentAssigning}
                                data-testid={`assign-assessment-class-${cls.id}`} onClick={() => handleAssignAssessment(cls.id, null)}>
                                <Send className="w-3 h-3 mr-1" /> Tum Sinifa Envanter Ata
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-slate-50 rounded-lg"><Building2 className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">Henuz sinif olusturulmamis</p></div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-3">Bireysel Ogrenci Atama</h3>
                    {students.length > 0 ? (
                      <div>
                        <div className="border rounded-lg max-h-72 overflow-y-auto mb-4">
                          {students.map(s => (
                            <div key={s.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 border-b last:border-b-0">
                              <Checkbox checked={selectedUsers.includes(s.id)} onCheckedChange={(checked) => setSelectedUsers(checked ? [...selectedUsers, s.id] : selectedUsers.filter(id => id !== s.id))} />
                              <div className="flex-1"><p className="font-medium text-sm">{s.name}</p><p className="text-xs text-slate-500">{s.email}</p></div>
                            </div>
                          ))}
                        </div>
                        <Button className="bg-gradient-to-r from-blue-600 to-emerald-600" disabled={selectedUsers.length === 0 || assessmentAssigning} data-testid="assign-individual-assessment-btn"
                          onClick={() => handleAssignAssessment(null, selectedUsers)}>
                          <Send className="w-4 h-4 mr-2" /> {assessmentAssigning ? 'Ataniyor...' : `${selectedUsers.length} Ogrenciye Envanter Ata`}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-slate-50 rounded-lg"><Users className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">Henuz ogrenci eklenmemis</p></div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===== REPORTS TAB (PDR/Admin only) ===== */}
            {canViewReports && (
              <TabsContent value="reports" className="space-y-6">
                {/* Student Reports */}
                <Card className="shadow-lg border-none">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Ogrenci Raporlari</CardTitle>
                    <CardDescription>Kurumunuzdaki tum ogrenci mizac raporlarini goruntueleyin</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {studentReports.length > 0 ? (
                      <div className="space-y-3">
                        <div className="text-sm text-slate-500 mb-4">
                          Toplam {studentReports.length} rapor bulundu
                        </div>
                        <div className="max-h-96 overflow-y-auto space-y-2">
                          {studentReports.map(report => (
                            <div key={report.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <p className="font-medium text-sm">{report.student_name}</p>
                                  {report.temperament_name && (
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">{report.temperament_name}</Badge>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500 mt-1">{report.student_email}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {report.created_at ? new Date(report.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button variant="outline" size="sm" className="bg-blue-50 border-blue-200" data-testid={`view-report-${report.id}`}
                                  onClick={() => navigate(`/report/${report.id}`)}>
                                  <Eye className="w-3 h-3 mr-1" /> Goruntule
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500 mb-2">Henuz ogrenci raporu bulunmuyor</p>
                        <p className="text-sm text-slate-400">Ogrenciler envanterleri tamamladikca raporlar burada gorunecek</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Class Reports */}
                <Card className="shadow-lg border-none">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-600" /> Sinif Mizac Analiz Raporlari</CardTitle>
                    <CardDescription>Sinif bazinda mizac profili ve analiz raporlari olusturun</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {classes.length > 0 ? (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {classes.map(cls => {
                          const classAnalytics = analytics?.class_analytics?.find(a => a.class_id === cls.id);
                          return (
                            <Card key={cls.id} className="border shadow-sm hover:shadow-md transition-all">
                              <CardContent className="p-5">
                                <div className="mb-4">
                                  <h4 className="font-bold text-lg">{cls.name}</h4>
                                  <p className="text-sm text-slate-500">{cls.grade_level}. Sinif</p>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                                  <div className="bg-blue-50 rounded-lg p-2 text-center">
                                    <p className="text-blue-700 font-bold text-lg">{cls.student_ids?.length || 0}</p>
                                    <p className="text-blue-600 text-xs">Ogrenci</p>
                                  </div>
                                  <div className="bg-emerald-50 rounded-lg p-2 text-center">
                                    <p className="text-emerald-700 font-bold text-lg">{classAnalytics?.students_with_reports || 0}</p>
                                    <p className="text-emerald-600 text-xs">Rapor</p>
                                  </div>
                                </div>
                                {classAnalytics?.distribution?.length > 0 && (
                                  <div className="mb-4 space-y-1">
                                    {classAnalytics.distribution.slice(0, 3).map((d, i) => (
                                      <div key={i} className="flex justify-between text-xs">
                                        <span className="text-slate-600 truncate mr-2">{d.name?.split(' / ')[0]}</span>
                                        <span className="font-medium text-slate-800">{d.percentage}%</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <Button className="w-full bg-gradient-to-r from-emerald-600 to-blue-600" size="sm"
                                  data-testid={`class-profile-report-${cls.id}`}
                                  onClick={() => loadClassProfileReport(cls)}>
                                  <BarChart3 className="w-3 h-3 mr-1" /> Sinif Profil Raporu
                                </Button>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-slate-50 rounded-lg"><Building2 className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">Henuz sinif olusturulmamis</p></div>
                    )}
                  </CardContent>
                </Card>

                {/* Class Profile Report Dialog */}
                <Dialog open={classReportDialogOpen} onOpenChange={setClassReportDialogOpen}>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-2xl flex items-center gap-2">
                        <BarChart3 className="w-6 h-6 text-emerald-600" />
                        {selectedClassForReport?.name} - Sinif Profil Raporu
                      </DialogTitle>
                      <DialogDescription>{selectedClassForReport?.grade_level}. Sinif Mizac Analizi</DialogDescription>
                    </DialogHeader>

                    {classProfileLoading ? (
                      <div className="flex flex-col items-center justify-center py-16">
                        <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
                        <p className="text-slate-600">AI sinif profil raporu olusturuluyor...</p>
                        <p className="text-sm text-slate-400 mt-1">Bu islem birkas saniye surebilir</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Distribution Chart */}
                        {classProfileReport?.analytics?.distribution?.length > 0 && (
                          <Card className="border">
                            <CardHeader className="pb-2"><CardTitle className="text-base">Mizac Dagilimi</CardTitle></CardHeader>
                            <CardContent>
                              <div className="grid md:grid-cols-2 gap-4">
                                <ResponsiveContainer width="100%" height={250}>
                                  <PieChart>
                                    <Pie data={classProfileReport.analytics.distribution.map(d => ({ name: d.name?.split(' / ')[0], value: d.count, percentage: d.percentage }))}
                                      cx="50%" cy="50%" outerRadius={80} fill="#8884d8" dataKey="value"
                                      label={({ name, percentage }) => `${name}: ${percentage}%`}>
                                      {classProfileReport.analytics.distribution.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                                    </Pie>
                                    <Tooltip />
                                  </PieChart>
                                </ResponsiveContainer>
                                <div className="space-y-2">
                                  {classProfileReport.analytics.distribution.map((d, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }}></div>
                                      <span className="text-sm flex-1">{d.name}</span>
                                      <span className="text-sm font-medium">{d.count} ({d.percentage}%)</span>
                                    </div>
                                  ))}
                                  <div className="pt-2 border-t mt-2">
                                    <p className="text-sm text-slate-600">Toplam: {classProfileReport.analytics.total_students} ogrenci</p>
                                    <p className="text-sm text-slate-600">Tamamlayan: {classProfileReport.analytics.students_with_reports}</p>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* AI Profile Report */}
                        {classProfileReport?.profile_report && (
                          <Card className="border">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base flex items-center gap-2">
                                <FileText className="w-4 h-4 text-blue-600" /> AI Sinif Profil Raporu
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {classProfileReport.profile_report}
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Student List with Temperaments */}
                        {classProfileReport?.students?.length > 0 && (
                          <Card className="border">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base flex items-center gap-2">
                                <Users className="w-4 h-4 text-emerald-600" /> Ogrenci Mizac Detaylari
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {classProfileReport.students.map(s => (
                                  <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                                    <div className="flex-1">
                                      <p className="font-medium text-sm">{s.name}</p>
                                      <p className="text-xs text-slate-500">{s.email}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {s.has_report ? (
                                        <>
                                          <Badge className="bg-blue-100 text-blue-700 text-xs">{s.temperament_name || 'Belirlendi'}</Badge>
                                          {s.report_id && (
                                            <Button variant="ghost" size="sm" className="text-blue-600 text-xs" onClick={() => { setClassReportDialogOpen(false); navigate(`/report/${s.report_id}`); }}>
                                              <Eye className="w-3 h-3" />
                                            </Button>
                                          )}
                                        </>
                                      ) : (
                                        <Badge variant="outline" className="text-slate-400 text-xs">Bekleniyor</Badge>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Temperament Detail Table */}
                        {classTemperamentReport?.student_details?.length > 0 && (
                          <Card className="border">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base flex items-center gap-2">
                                <BarChart3 className="w-4 h-4 text-purple-600" /> Detayli Mizac Analizi
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b">
                                      <th className="text-left py-2 px-3">Ogrenci</th>
                                      <th className="text-left py-2 px-3">Birincil Mizac</th>
                                      <th className="text-left py-2 px-3">Ikincil Mizac</th>
                                      <th className="text-center py-2 px-3">Durum</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {classTemperamentReport.student_details.map(s => (
                                      <tr key={s.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => setExpandedStudent(expandedStudent === s.id ? null : s.id)}>
                                        <td className="py-2 px-3 font-medium">{s.name}</td>
                                        <td className="py-2 px-3">
                                          {s.primary_name ? <Badge className="bg-blue-100 text-blue-700 text-xs">{s.primary_name?.split(' / ')[0]}</Badge> : <span className="text-slate-400">-</span>}
                                        </td>
                                        <td className="py-2 px-3">
                                          {s.secondary_name ? <Badge variant="outline" className="text-xs">{s.secondary_name?.split(' / ')[0]}</Badge> : <span className="text-slate-400">-</span>}
                                        </td>
                                        <td className="py-2 px-3 text-center">
                                          {s.has_report ? (
                                            <div className="flex items-center justify-center gap-1">
                                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Tamamlandi</Badge>
                                              {s.report_id && (
                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); setClassReportDialogOpen(false); navigate(`/report/${s.report_id}`); }}>
                                                  <Eye className="w-3 h-3 text-blue-600" />
                                                </Button>
                                              )}
                                            </div>
                                          ) : (
                                            <Badge variant="outline" className="text-slate-400 text-xs">Bekleniyor</Badge>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="mt-3 text-xs text-slate-500">
                                {classTemperamentReport.completed_count}/{classTemperamentReport.total_students} ogrenci envanteri tamamladi
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </TabsContent>
            )}
          </Tabs>
        )}
      </main>
    </div>
  );
}
